import React, {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { Box } from '../Box';
import { Icon } from '../Icon';
import { Modal } from '../Modal';
import { Text } from '../Typography';
import * as styles from './ImageGalleryModal.styles';
import type { ImageGalleryModalProps } from './ImageGalleryModal.types';

/**
 * ImageGalleryModal component for displaying images in a fullscreen carousel modal.
 * Supports keyboard navigation (arrow keys), swipe gestures, and accessibility features.
 */
export const ImageGalleryModal = ({
  id,
  isOpen,
  onClose,
  images,
  initialIndex = 0,
  showDots = true,
  showArrows = true,
  showCounter = true,
  className,
  ariaLabel = 'Image gallery',
}: ImageGalleryModalProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Reset to initial index when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex]);

  const handlePrev = useCallback(
    (e?: MouseEvent | KeyboardEvent) => {
      e?.stopPropagation();
      setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    },
    [images.length]
  );

  const handleNext = useCallback(
    (e?: MouseEvent | KeyboardEvent) => {
      e?.stopPropagation();
      setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    },
    [images.length]
  );

  const handleGoTo = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handlePrev, handleNext]);

  const currentImage = images[currentIndex];
  const hasMultipleImages = images.length > 1;

  return (
    <Modal
      id={`image-gallery-${id}`}
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      closeOnOutsideClick
      closeOnEscape
      showCloseButton
      closeButtonStyles={styles.closeButtonOverrideStyles}
      scrollLayerStyles={styles.scrollLayerOverrideStyles}
      containerStyles={styles.containerOverrideStyles}
      contentStyles={styles.contentOverrideStyles}
      className={className}
      ariaLabel={ariaLabel}
    >
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        height="100%"
        position="relative"
        css={styles.galleryBackgroundStyles}
      >
        {/* Image Counter */}
        {showCounter && hasMultipleImages && (
          <Box
            position="absolute"
            top="var(--spacing-4)"
            left="50%"
            zIndex={10}
            css={styles.counterStyles}
          >
            <Text size="sm" color="inherit">
              {currentIndex + 1} / {images.length}
            </Text>
          </Box>
        )}

        {/* Main Image Container */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flex="1"
          overflow="hidden"
          position="relative"
        >
          {/* Previous Button */}
          {showArrows && hasMultipleImages && (
            <button
              type="button"
              onClick={handlePrev}
              css={styles.prevButtonStyles}
              aria-label="Previous image"
            >
              <Icon variant="AngleLeft" size="medium" />
            </button>
          )}

          {/* Current Image */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
            height="100%"
            p="var(--spacing-4)"
          >
            {currentImage && (
              <img
                src={currentImage.src}
                alt={currentImage.alt || `Image ${currentIndex + 1}`}
                css={styles.imageStyles}
              />
            )}
          </Box>

          {/* Next Button */}
          {showArrows && hasMultipleImages && (
            <button
              type="button"
              onClick={handleNext}
              css={styles.nextButtonStyles}
              aria-label="Next image"
            >
              <Icon variant="AngleRight" size="medium" />
            </button>
          )}
        </Box>

        {/* Caption */}
        {currentImage?.caption && (
          <Box
            position="absolute"
            bottom="var(--spacing-16)"
            left="50%"
            zIndex={10}
            css={styles.captionStyles}
          >
            <Text size="sm" color="inherit">
              {currentImage.caption}
            </Text>
          </Box>
        )}

        {/* Dots Navigation */}
        {showDots && hasMultipleImages && (
          <Box
            display="flex"
            position="absolute"
            bottom="var(--spacing-4)"
            left="50%"
            gap="var(--spacing-2)"
            zIndex={10}
            css={styles.dotsContainerPositionStyles}
          >
            {images.map((_, index) => (
              <button
                key={`dot-${index}`}
                type="button"
                onClick={() => handleGoTo(index)}
                css={[
                  styles.dotStyles,
                  index === currentIndex && styles.dotActiveStyles,
                ]}
                aria-label={`Go to image ${index + 1}`}
                aria-current={index === currentIndex ? 'true' : undefined}
              />
            ))}
          </Box>
        )}
      </Box>
    </Modal>
  );
};

ImageGalleryModal.displayName = 'ImageGalleryModal';

export default ImageGalleryModal;
